import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useDarkMode } from "@/shared/hooks/useDarkMode";

import playIcon from "@/assets/icons/play.svg";
import pauseIcon from "@/assets/icons/pause.svg";
import restartIcon from "@/assets/icons/restart.svg";
import playWhiteIcon from "@/assets/icons/playWhite.svg";
import pauseWhiteIcon from "@/assets/icons/pauseWhite.svg";
import restartWhiteIcon from "@/assets/icons/restartWhite.svg";

import styles from "./HeaderBar.module.css";

interface TimerProps {
  /** Current elapsed time in seconds */
  elapsedSeconds: number;
  /** Whether the timer is currently running */
  isRunning: boolean;
  /** Called when play/pause button is clicked while paused */
  onStart: () => void;
  /** Called when play/pause button is clicked while running */
  onStop: () => void;
  /** Called when reset button is clicked */
  onReset: () => void;
}

/**
 * Formats total seconds into HH:MM:SS display format.
 */
function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Timer display component with play/pause and reset controls.
 *
 * This is a presentational component - all state management should be
 * handled by the parent using the useTimer hook.
 *
 * @example
 * ```tsx
 * const { elapsedSeconds, isRunning, start, stop, reset } = useTimer({
 *   onStop: (seconds) => openAddEntryDialog(seconds)
 * });
 *
 * <Timer
 *   elapsedSeconds={elapsedSeconds}
 *   isRunning={isRunning}
 *   onStart={start}
 *   onStop={stop}
 *   onReset={reset}
 * />
 * ```
 */
export function Timer({
  elapsedSeconds,
  isRunning,
  onStart,
  onStop,
  onReset,
}: TimerProps) {
  const { darkMode } = useDarkMode();
  const [isHovered, setIsHovered] = useState(false);

  const handlePlayPauseClick = () => {
    if (isRunning) {
      onStop();
    } else {
      onStart();
    }
  };

  return (
    <div
      className={cn(
        "flex items-center rounded-md border transition-colors h-10",
        "border-border bg-card",
        isHovered && "bg-secondary"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Play/Pause button with time display */}
      <Button
        variant="ghost"
        className={cn(
          "justify-start text-left font-normal flex-1 h-full",
          "border-0 rounded-r-none hover:bg-transparent",
          "text-foreground"
        )}
        onClick={handlePlayPauseClick}
      >
        {isRunning ? (
          <img
            src={darkMode ? pauseWhiteIcon : pauseIcon}
            alt="Pause timer"
            className={styles.iconSmall}
          />
        ) : (
          <img
            src={darkMode ? playWhiteIcon : playIcon}
            alt="Start timer"
            className={styles.iconSmall}
          />
        )}
        {formatTime(elapsedSeconds)}
      </Button>

      {/* Divider */}
      <div className="flex items-center self-stretch py-2">
        <div className="w-px h-full bg-border" />
      </div>

      {/* Reset button */}
      <button
        className="flex items-center justify-center w-10 h-full rounded-r-md transition-opacity opacity-70 hover:opacity-100"
        onClick={onReset}
        aria-label="Reset timer"
        type="button"
      >
        <img
          src={darkMode ? restartWhiteIcon : restartIcon}
          alt="Reset"
          className={styles.iconSmall}
        />
      </button>
    </div>
  );
}
